/* The bottom bar of the home screen version: each tab remembers where you were, and tapping the open tab takes you
   back to its start. */
(() => {
  const html = document.documentElement;
  if (!html.classList.contains('m-app')) return;
  const bar = document.querySelector('[data-app-tabs]');
  if (!bar) return;
  const here = () => location.pathname + location.search.replace(/[?&]app=1/, '').replace(/^&/, '?');
  const memory = (tab) => { try { return sessionStorage.getItem(`maison:tab:${tab}`); } catch (e) { return null; } };
  const active = bar.dataset.appTabs;
  // Only Shop and Search have somewhere to go back to. Saved on leaving, because Search changes its address as you type.
  if (active === 'shop' || active === 'search') {
    window.addEventListener('pagehide', () => { try { sessionStorage.setItem(`maison:tab:${active}`, here()); } catch (e) { /* tabs simply start fresh */ } });
  }
  bar.querySelectorAll('[data-tab]').forEach((tab) => {
    const name = tab.dataset.tab;
    tab.dataset.root = tab.getAttribute('href');
    if (name !== active && memory(name)) tab.setAttribute('href', memory(name));
    tab.addEventListener('click', (event) => {
      if (name !== active) return;
      event.preventDefault();
      const atStart = location.pathname.replace(/\/$/, '') === tab.dataset.root.replace(/\/$/, '') && !location.search.replace(/[?&]app=1/, '');
      if (atStart) (window.innerWidth >= 990 && document.querySelector('.page-wrapper') || window).scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      else location.href = tab.dataset.root;
    });
  });
  // A count of nothing is not shown.
  const bagCount = bar.querySelector('[data-mh-count]');
  const showBag = () => {
    const n = Number(bagCount.textContent) || 0;
    bagCount.hidden = n === 0;
    bagCount.closest('[data-tab]').setAttribute('aria-label', n ? `Bag, ${n} ${n === 1 ? 'piece' : 'pieces'}` : 'Bag');
  };
  new MutationObserver(showBag).observe(bagCount, { childList: true, characterData: true, subtree: true });
  showBag();
  const typing = (el) => el && (el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' && !/^(checkbox|radio|button|submit|range)$/.test(el.type)));
  // The keyboard needs the room, and on some phones it would otherwise push the bar up the screen.
  document.addEventListener('focusin', (event) => { if (typing(event.target)) html.classList.add('m-app-typing'); });
  document.addEventListener('focusout', () => setTimeout(() => { if (!typing(document.activeElement)) html.classList.remove('m-app-typing'); }, 80));
  document.querySelector('[data-app-back]')?.addEventListener('click', () => {
    const fromHere = document.referrer && new URL(document.referrer).host === location.host;
    if (fromHere && history.length > 1) history.back();
    else location.href = active === 'shop' ? '/pages/shop' : '/';
  });
})();

/* The note offering a home screen icon: phones only, after a little looking around, never on a product page, the bag or checkout.
   ?app-note=android or ?app-note=ios shows it at once, for checking the design. */
(() => {
  const KEY = 'maison:app-note';
  const PAGES_BEFORE_NOTE = 3;
  const QUIET_DAYS = 60;
  const WAIT_MS = 6000;
  const forced = new URLSearchParams(location.search).get('app-note');

  const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch (e) { return null; } };
  const write = (state) => { try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* no storage: the note simply is not remembered */ } };

  const ua = navigator.userAgent;
  const installed = document.documentElement.classList.contains('m-app');
  // Phones and tablets alike. iPads say they are Macs, but Macs have no touch screen.
  const iPad = /iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const phone = window.matchMedia('(pointer: coarse)').matches || iPad;
  const iphone = /iPhone|iPod/.test(ua) || iPad;
  // Instagram, Facebook and similar apps open pages in their own browser, which cannot add to the home screen.
  const inAppBrowser = /Instagram|FBAN|FBAV|Line\/|; wv\)/.test(ua);

  const state = read();
  if (!forced) {
    if (state === null || installed || !phone || inAppBrowser || state.added) return;
    state.pages = (state.pages || 0) + 1;
    write(state);
    if (state.hiddenUntil && Date.now() < state.hiddenUntil) return;
    if (state.pages < PAGES_BEFORE_NOTE) return;
    if (/^\/(cart|checkout|products|search)/.test(location.pathname)) return;
  }

  const busy = () => Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"], .mfl-card')).some((el) => {
    const style = getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > 0.01;
  });

  const close = (note, nextState) => {
    write(nextState);
    note.classList.remove('is-on');
    setTimeout(() => note.remove(), 400);
  };

  const show = (mode) => {
    if (document.querySelector('.m-app-note')) return;
    const note = document.createElement('aside');
    note.className = 'm-app-note';
    note.setAttribute('aria-label', 'Add Lunafreya to your home screen');
    note.innerHTML = `
      <span class="m-app-note__star" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M12 1c.9 7 3.6 10 11 11-7.4 1-10.100 4-11 11-.9-7-3.600-10-11-11 7.400-1 10.100-4 11-11Z"/></svg></span>
      <div class="m-app-note__words">
        <p class="m-app-note__title">Keep Lunafreya on your phone</p>
        <p class="m-app-note__text">${mode === 'ios'
          ? 'Tap Share in your browser, then Add to Home Screen.'
          : 'Opens like an app. Nothing to download.'}</p>
      </div>
      ${mode === 'ios' ? '' : '<button type="button" class="m-app-note__add" data-add>Add</button>'}
      <button type="button" class="m-app-note__close" data-close aria-label="Not now"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5 5 19"/></svg></button>`;
    document.body.append(note);
    requestAnimationFrame(() => note.classList.add('is-on'));
    note.querySelector('[data-close]').addEventListener('click', () => {
      close(note, { ...(read() || {}), hiddenUntil: Date.now() + QUIET_DAYS * 86400000 });
    });
    note.querySelector('[data-add]')?.addEventListener('click', async () => {
      const prompt = window.__lfInstall;
      if (!prompt) return;
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      window.__lfInstall = null;
      close(note, { ...(read() || {}), ...(outcome === 'accepted' ? { added: true } : { hiddenUntil: Date.now() + QUIET_DAYS * 86400000 }) });
    });
  };

  window.addEventListener('appinstalled', () => {
    write({ ...(read() || {}), added: true });
    document.querySelector('.m-app-note')?.remove();
  });

  if (forced) { show(forced === 'ios' ? 'ios' : 'android'); return; }
  setTimeout(() => {
    if (busy()) return;
    // Android offers its own install step, and only then is there something for the button to do.
    if (iphone) show('ios');
    else if (window.__lfInstall) show('android');
  }, WAIT_MS);
})();
